import Backdrop from "@mui/material/Backdrop"
import Grow from "@mui/material/Grow"
import Modal from "@mui/material/Modal"
import withStyles from '@mui/styles/withStyles';
import { Trash2, X } from "lucide-react"
import Question from "components/Question"
import CtaButton from "components/redesign/CtaButton"
import EventSeriesDialog, { seriesOptions, singleOption } from "components/EventSeriesDialog"
import { color, font, radius, shadow, state, type } from "common/tokens"
import { find, get, map } from 'lodash'
import moment from "moment"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

// Stops along a smoothstep curve rather than a straight line, so neither
// layer shows the band a linear two-stop gradient leaves behind.
const COVER_FADE = `linear-gradient(to top,
  ${color.SURFACE} 0%,
  rgba(255, 255, 255, 0.98) 14%,
  rgba(255, 255, 255, 0.92) 27%,
  rgba(255, 255, 255, 0.82) 39%,
  rgba(255, 255, 255, 0.66) 51%,
  rgba(255, 255, 255, 0.47) 63%,
  rgba(255, 255, 255, 0.28) 74%,
  rgba(255, 255, 255, 0.13) 85%,
  rgba(255, 255, 255, 0.04) 93%,
  rgba(255, 255, 255, 0) 100%)`;

const EDGE_SHADOW = `radial-gradient(farthest-side at 50% 100%,
  rgba(0, 0, 0, 0.15) 0%,
  rgba(0, 0, 0, 0.125) 18%,
  rgba(0, 0, 0, 0.095) 34%,
  rgba(0, 0, 0, 0.065) 49%,
  rgba(0, 0, 0, 0.04) 63%,
  rgba(0, 0, 0, 0.021) 76%,
  rgba(0, 0, 0, 0.008) 88%,
  rgba(0, 0, 0, 0) 100%)`;

const styles = theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  paper: {
    transformOrigin: 'center center',
    background: color.SURFACE,
    borderRadius: radius.MEDIA_CARD,
    boxShadow: shadow.CARD,
    width: 560,
    maxWidth: '100%',
    maxHeight: '90vh',
    outline: 0,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '24px 32px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 20px 12px',
    },
    borderBottom: `1px solid ${color.DIVIDER}`,
  },
  title: {
    ...type.panelTitle,
    color: color.TEXT,
    margin: 0,
  },
  close: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 4,
    cursor: 'pointer',
    display: 'flex',
    color: color.TEXT_MUTED,
  },
  // The shared Question inputs are MUI-based and default to a blue focus ring.
  // These overrides are scoped to this dialog so surveys and quizzes, which are
  // not redesigned yet, keep their current look.
  form: {
    padding: '16px 32px 0',
    [theme.breakpoints.down('sm')]: {
      padding: '16px 20px 0',
    },
    overflowY: 'auto',
    flex: '1 1 auto',

    // Pure-CSS scroll shadow: the opaque layer scrolls with the content and
    // covers the shadow once the last field is in view. Both layers ease out
    // over many stops — a two-stop gradient fades in a straight line, which
    // the eye reads as an edge partway up. The shadow is a flattened radial so
    // it lifts off the bottom and thins towards the corners.
    backgroundImage: `${COVER_FADE}, ${EDGE_SHADOW}`,
    backgroundPosition: 'bottom',
    backgroundSize: '100% 56px, 100% 26px',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'local, scroll',

    '& .MuiInputBase-root': {
      backgroundColor: color.SURFACE,
      borderRadius: radius.CONTROL,
      minHeight: 46,
      border: `1px solid ${color.BORDER} !important`,
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      boxShadow: 'none',
    },
    '& .MuiInputBase-root:hover:not(.Mui-disabled)': {
      borderColor: `${color.TEXT_MUTED} !important`,
    },
    '& .MuiInputBase-root.Mui-focused, & .MuiInputBase-root:focus-within': {
      borderColor: `${color.ACCENT} !important`,
      boxShadow: `0 0 0 3px ${color.ACCENT_TINT} !important`,
    },
    '& .radio-toolbar input[type="radio"]:focus-visible + label': {
      borderColor: `${color.ACCENT} !important`,
      boxShadow: `0 0 0 3px ${color.ACCENT_TINT}`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    // Disabled follows the convention shadcn/ui and Tailwind UI settled on: the
    // control keeps its own colours and fades as a whole, so the value stays
    // readable and the field never becomes a grey slab. Text colour is pinned
    // back to full strength so the fade is applied once, not twice.
    '& .MuiInputBase-root.Mui-disabled': {
      opacity: state.DISABLED_OPACITY,
      cursor: 'not-allowed',
    },
    '& .MuiInputBase-root.Mui-disabled .MuiInputBase-input, & .MuiInputBase-root.Mui-disabled .MuiSelect-select': {
      color: color.TEXT,
      cursor: 'not-allowed',
    },
    '& .MuiInputBase-input': {
      ...type.body,
      color: color.TEXT,
      padding: '11px 14px',
    },
    '& .MuiSelect-select': {
      padding: '11px 14px',
    },
    '& .MuiSelect-icon, & .MuiIconButton-root': {
      color: color.TEXT_MUTED,
    },
    '& .MuiIconButton-root.Mui-disabled': {
      color: color.TEXT_MUTED,
    },
    '& textarea': {
      ...type.body,
      color: color.TEXT,
    },

    // Yes / No options follow the selectable-option pattern: outlined by
    // default, accent tint when chosen.
    '& .radio-toolbar label': {
      ...type.body,
      background: color.SURFACE,
      color: color.TEXT,
      border: `1.5px solid ${color.BORDER} !important`,
      borderRadius: `${radius.CTA} !important`,
      padding: '11px 20px !important',
      boxShadow: 'none',
      transition: 'all 0.15s ease',
    },
    '& .radio-toolbar label:hover': {
      background: color.SURFACE,
      borderColor: `${color.ACCENT_TEXT} !important`,
    },
    '& .radio-toolbar input[type="radio"]:checked + label': {
      background: color.ACCENT_TINT,
      borderColor: `${color.ACCENT} !important`,
      color: color.TEXT,
      fontWeight: font.BOLD,
    },
    '& .radio-toolbar .notSelected + label': {
      background: color.SURFACE,
      color: color.TEXT_MUTED,
    },
    '& .radio-toolbar input[type="radio"]:checked.notSelected + label': {
      background: color.ACCENT_TINT,
      color: color.TEXT,
    },
    '& .radio-toolbar input[type="radio"]:disabled + label': {
      backgroundColor: color.FIELD_BG,
      color: color.TEXT_SUBTLE,
      borderColor: `${color.DIVIDER} !important`,
    },
  },
  repeatContainer: {
    display: 'flex',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 0,
    },
  },
  repeatBlock: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    padding: '20px 32px 24px',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      gap: 16,
      padding: '16px 20px 20px',
    },
    borderTop: `1px solid ${color.DIVIDER}`,
  },
  textButton: {
    ...type.body,
    fontWeight: font.SEMIBOLD,
    appearance: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px 4px',
    color: color.PURPLE,
  },
  deleteButton: {
    color: color.DANGER,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
  },
});

// Most calls are booked in the morning, and a picker that opens on midnight
// reads as "nothing chosen yet".
const DEFAULT_HOUR = 10;

const labelStyling = {
  fontSize: 'var(--ui-text-eyebrow)',
  lineHeight: 'var(--ui-leading-eyebrow)',
  letterSpacing: '1.1px',
  fontWeight: 'var(--ui-weight-semibold)',
  textTransform: 'uppercase',
  color: 'var(--ui-text-muted)',
};

const AddEventForm = ({
  classes,
  events,
  open,
  handleClose,
  sessions,
  mentorshipId,
  submitEvent,
  profileId,
  cancelEvent,
  mentorshipGroupId,
  onSubmitted,
}) => {
  const { t } = useTranslation();
  const answerOptions = [
    { key: true, label: t('yes'), displayOrder: 1 },
    { key: false, label: t('no'), displayOrder: 2 },
  ]

  const [newEvent, setNewEvent] = useState({})
  // scheduledTime carries the date and the time together, so it can never be
  // nulled to clear just the time. This is what the time field shows instead.
  const [timeSet, setTimeSet] = useState(true)
  const [errors, setErrors] = useState({});
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [appliedTo, setAppliedTo] = useState(null);

  const isEditEvent = get(open, "eventType") === "editEvent"

  const repeatOptions = [
    'Does not repeat',
    'Weekly',
    'Once every 2 weeks',
    'Monthly'
  ]

  const handleChange = (name) => (value) => {
    const updatedEvent = {
      ...newEvent,
      [name]: value
    }

    // If eventFrequency is changed to a recurring option, clear sessionName
    if (name === "eventFrequency" && value && value !== "Does not repeat") {
      updatedEvent.sessionName = undefined;
    }

    setNewEvent(updatedEvent);
  }

  // The API takes a single scheduled_time; the form asks for the date and the
  // time separately and recombines them here.
  const scheduled = newEvent.scheduledTime ? moment(newEvent.scheduledTime) : null;

  const handleDatePart = date => {
    if (!date) return handleChange("scheduledTime")(null);
    const next = moment(date);
    const base = scheduled || moment().startOf('day').hour(DEFAULT_HOUR);
    next.hour(base.hour()).minute(base.minute()).second(0);
    handleChange("scheduledTime")(next);
  };

  const handleTimePart = time => {
    if (!time) {
      setTimeSet(false);
      if (!scheduled) return;
      return handleChange("scheduledTime")(scheduled.clone().startOf('day').hour(DEFAULT_HOUR));
    }
    setTimeSet(true);
    const picked = moment(time);
    const next = (scheduled ? scheduled.clone() : moment().startOf('day').hour(DEFAULT_HOUR));
    next.hour(picked.hour()).minute(picked.minute()).second(0);
    handleChange("scheduledTime")(next);
  };

  const getEventFromEvents = eventId => find(events, event => event.id === eventId)

  const handleConfirmDelete = async (applyTo) => {
    setDeleting(true)
    try {
      await cancelEvent(getEventFromEvents(open.EventId), applyTo, mentorshipId)
      setDeleteDialogOpen(false)
      handleClose()
    } finally {
      setDeleting(false)
    }
  }

  useEffect(() => {
    setTimeSet(true);
    if (open.eventType === 'editEvent') {
      const eventInit = getEventFromEvents(open.EventId)
      const isPairGroupCallValue = answerOptions.find(option => option.key === eventInit.isPairGroupCall);

      setNewEvent({
        ...eventInit,
        // The API stores "Does not repeat" as null, so the dropdown would open
        // empty on an event that simply never repeats.
        eventFrequency: eventInit.eventFrequency || repeatOptions[0],
        isPairGroupCall: isPairGroupCallValue,
      });
    } else {
      // Almost every event booked here is a mentoring call, so Yes starts selected.
      setNewEvent({
        isPairGroupCall: answerOptions.find(option => option.key === true),
        scheduledTime: moment().second(0).millisecond(0),
      });
    }
    return function cleanup() {
      setNewEvent({})
      setErrors({})
    }
  }, [open])

  const titleQuestion = { label: t('eventTitleLabel'), questionType: 'Text', mandatory: true };
  const dateQuestion = { label: t('selectDate'), questionType: 'Date', openTo: 'date', mandatory: true };
  const timeQuestion = { label: t('selectTimeLabel'), questionType: 'Time' };
  const descriptionQuestion = { label: t('descriptionLabel'), questionType: 'TextBox' };
  const isPairGroupCallQuestion = {
    label: t('isMentoringCallLabel'),
    questionType: 'SingleChoiceRadio',
    answerOptions: answerOptions,
    mandatory: true,
  };
  const repeatQuestion = {
    label: t('repeatLabel'),
    questionType: 'SimpleDropDown',
    answerOptions: repeatOptions,
    mandatory: true,
  }
  const isRecurring = Boolean(newEvent.eventFrequency) && newEvent.eventFrequency !== "Does not repeat";
  // A session belongs to one specific call, so no recurring event may carry
  // one — including an already-saved series being edited, whose frequency the
  // form has not re-derived.
  const blocksSession = isRecurring || Boolean(newEvent.isRecurringEvent);

  // A disabled field says why it is disabled, so the form does not look broken.
  const sessionQuestion = {
    label: t('selectSession'),
    questionType: 'SimpleDropDown',
    answerOptions: map(sessions, (session) => session.title),
    helpText: blocksSession ? t('sessionOnlyForSingleEvents') : undefined,
    mandatory: !blocksSession,
  }
  const repeatEndsQuestion = {
    label: t('repeatEndsOnLabel'),
    questionType: 'Date',
    openTo: 'date',
    helpText: isRecurring ? undefined : t('repeatEndsNeedsFrequency'),
    mandatory: isRecurring,
  }

  const submitTransformedEvent = async (applyTo = "THIS") => {
    const transformedEvent = {
      id: newEvent.id,
      mentorship: mentorshipId,
      mentor_group: mentorshipGroupId,
      title: newEvent.title,
      // Empty string, not null: session_name is a non-null CharField and the
      // recurring branch of the update endpoint writes request.data straight
      // into objects.create(), so a null becomes an IntegrityError.
      session_name: blocksSession ? "" : newEvent.sessionName,
      description: newEvent.description,
      event_frequency: newEvent.eventFrequency,
      is_pair_group_call: newEvent.isPairGroupCall ? newEvent.isPairGroupCall.key : false,
      is_recurring_event: newEvent.isRecurringEvent,
      repeat_end_date: newEvent.repeatEndDate,
      scheduled_time: newEvent.scheduledTime,
      created_by: profileId,
      apply_to_recurring: applyTo,
    };

    setSubmitting(true);
    try {
      await submitEvent(mentorshipId, mentorshipGroupId, transformedEvent);
      setRecurringDialogOpen(false);
      handleClose();
      if (onSubmitted) {
        onSubmitted(isEditEvent);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    setErrors({});
    let validationErrors = {};

    if (!newEvent.title) validationErrors.title = t('This field is required');
    if (!newEvent.isPairGroupCall) validationErrors.isPairGroupCall = t('This field is required');
    if (!newEvent.scheduledTime) validationErrors.scheduledTime = t('This field is required');
    if (!newEvent.eventFrequency) validationErrors.eventFrequency = t('This field is required');

    if (isRecurring && !newEvent.repeatEndDate) {
      validationErrors.repeatEndDate = t('This field is required');
    }

    if (!blocksSession && !newEvent.sessionName) {
      validationErrors.sessionName = t('This field is required');
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isEditEvent && newEvent.isRecurringEvent) {
      setAppliedTo(null);
      setRecurringDialogOpen(true);
    } else {
      submitTransformedEvent("THIS");
    }
  }

  return (
    <>
      {/* Opens the way the redesign's shared Dialog does, so every modal in the
          app arrives with the same movement. */}
      <Modal
        className={classes.modal}
        open={Boolean(open.state)}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{timeout: 220}}
      >
        <Grow in={Boolean(open.state)} timeout={{enter: 240, exit: 160}}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <h2 className={classes.title}>
              {isEditEvent ? t('updateTheEvent') : t('createNewEvent')}
            </h2>
            <button type={'button'} className={classes.close} aria-label={t('cancelLabel')} onClick={handleClose}>
              <X size={20} strokeWidth={2}/>
            </button>
          </div>

          <div className={classes.form}>
            <Question question={titleQuestion} labelStyling={labelStyling} showBorder onAnswered={handleChange("title")} value={newEvent.title} error={errors.title}/>
            <Question question={isPairGroupCallQuestion} labelStyling={labelStyling} showBorder onAnswered={handleChange("isPairGroupCall")} value={newEvent.isPairGroupCall} error={errors.isPairGroupCall}/>
            <div className={classes.repeatContainer}>
              <div className={classes.repeatBlock}>
                <Question question={dateQuestion} labelStyling={labelStyling} showBorder onAnswered={handleDatePart} value={newEvent.scheduledTime || null} error={errors.scheduledTime}/>
              </div>
              <div className={classes.repeatBlock}>
                <Question question={timeQuestion} labelStyling={labelStyling} showBorder onAnswered={handleTimePart} value={timeSet ? (newEvent.scheduledTime || null) : null}/>
              </div>
            </div>

            <div className={classes.repeatContainer}>
              <div className={classes.repeatBlock}>
                <Question
                  question={repeatQuestion}
                  labelStyling={labelStyling}
                  showBorder
                  onAnswered={handleChange("eventFrequency")}
                  value={newEvent.eventFrequency}
                  error={errors.eventFrequency}
                />
              </div>
              <div className={classes.repeatBlock}>
                <Question
                  question={repeatEndsQuestion}
                  labelStyling={labelStyling}
                  showBorder
                  onAnswered={handleChange("repeatEndDate")}
                  value={newEvent.repeatEndDate}
                  disabled={!isRecurring}
                  error={errors.repeatEndDate}
                />
              </div>
            </div>

            <Question
              question={sessionQuestion}
              labelStyling={labelStyling}
              showBorder
              onAnswered={handleChange("sessionName")}
              value={blocksSession ? undefined : newEvent.sessionName}
              disabled={blocksSession}
              error={errors.sessionName}
            />
            <Question question={descriptionQuestion} labelStyling={labelStyling} showBorder onAnswered={handleChange("description")} value={newEvent.description} height={'100px'}/>
          </div>

          <div className={classes.footer}>
            <CtaButton
              size={'large'}
              variant={'primary'}
              loading={submitting}
              label={isEditEvent ? t('updateEvent') : t('createEventCta')}
              onClick={handleSubmit}
            />
            <button type={'button'} className={classes.textButton} onClick={handleClose}>
              {t('cancelLabel')}
            </button>
            {isEditEvent && (
              <button
                type={'button'}
                className={`${classes.textButton} ${classes.deleteButton}`}
                disabled={deleting}
                onClick={() => {
                  setAppliedTo(null)
                  setDeleteDialogOpen(true)
                }}
              >
                <Trash2 size={16} strokeWidth={2}/>
                {t('deleteEvent')}
              </button>
            )}
          </div>
        </div>
        </Grow>
      </Modal>

      <EventSeriesDialog
        open={recurringDialogOpen}
        title={t('updateEvent')}
        prompt={t('applyChangesPrompt')}
        options={seriesOptions(t)}
        chosen={appliedTo}
        busy={submitting}
        onChoose={submitTransformedEvent}
        onDismiss={() => setRecurringDialogOpen(false)}
      />

      <EventSeriesDialog
        open={deleteDialogOpen}
        title={t('deleteEvent')}
        prompt={newEvent.isRecurringEvent ? t('deleteRecurringEventPrompt') : t('deleteEventConfirmPrompt')}
        options={newEvent.isRecurringEvent ? seriesOptions(t) : singleOption(t('deleteCaps'))}
        chosen={appliedTo}
        chosenVariant={'danger'}
        busy={deleting}
        onChoose={handleConfirmDelete}
        onDismiss={() => setDeleteDialogOpen(false)}
      />
    </>
  );
};

export default withStyles(styles)(AddEventForm);
